import { createContext, useContext, useEffect, useReducer, useRef, ReactNode } from 'react';
import { useEffectOnce } from 'react-use';
import { DraftOfferType, DraftType, PricingModifiers } from '@/enterprise/data/useDraftOffer';
import useGroupByBuyer, { sortTakerMarkets } from '@/enterprise/data/useGroupByBuyer';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { Schedule } from '@/enterprise/features/offerModal/inputs/SchedulingInput';
import { OfferBuilderStepPath } from './OfferBuilderSteps';
import { defaultSchedule } from './OfferReview/EditScheduleModal';
import { OfferSubmissionError } from './OfferReview/useSubmitOffers/useSubmitOffers';
import {
  buildTakerMarketKey,
  computeWorkingTakerMarketsAfterToggle,
  createMixedRateMap,
  createUniformRateMap,
  ensureWorkingTakerMarkets,
  filterBySelectedTakerMarkets,
  getNameYourRateMarkets,
  resetRatesForWorkingTakerMarkets,
  updateWorkingTakerMarketsWithRates,
  clearTakerMarketErrorsForKeys,
} from './utils';
import type { PricingInformationMap } from './getPricingInformationForTracking';
import type { RateInfo, RateMap } from './rateHelpers';

export interface OfferBuilderError {
  type: 'NO_TAKER_MARKETS_SELECTED' | 'NO_FREQUENCY_SELECTED';
}

export interface TakerMarketError {
  takerMarketKey: string;
  type: 'NO_RATE_SELECTED';
}

export type RateType = 'APR' | 'DISC';

export type WorkingTakerMarket = Pick<
  TakerMarket,
  'currency' | 'makerOrganizationUuid' | 'marketUuid' | 'takerDivisionUuid'
> & {
  rate: number | undefined;
  rateType: RateType | undefined;
  pricingRateUuid?: string | undefined;
  suggestedRate?: number | undefined;
  suggestRateType?: RateType | undefined;
  customRate?: number | undefined;
  customRateType?: RateType | undefined;
  pricingStrategy?: OfferBuilderPricingStrategy | undefined;
  isIndividuallyOverridden?: boolean | undefined;
  projectedClearingPercentage?: number | undefined;
};

export type OfferBuilderVariant = 'standard' | 'draft' | 'acquisition';
export type OfferBuilderFrequency = 'oneTime' | 'recurring';
export type OfferBuilderPricingStrategy = 'nameYourRate' | 'trendingRate' | 'expressAccept';
export type OfferBuilderLocation =
  | 'homepage'
  | 'division'
  | 'offer-activity'
  | 'markets'
  | 'customers'
  | 'invoice-central';

export interface RateInformation {
  rate: number;
  rateType: 'APR' | 'DISC' | 'REF';
  spread?: number;
  referenceRate?: number;
  rateName?: string;
}

export interface RateInformations {
  marketUuid: string;
  supplierDivisionUuid: string;
  rateInformation: RateInformation;
}

export interface PreUpgradeRateEntry {
  takerMarketKey: string;
  rateInfo: RateInfo;
}

export interface DraftOfferProperties {
  draftOfferUuid: string;
  offerType: DraftOfferType;
  draftType: DraftType;
  termExpirationDate?: string;
  rateInformations: RateInformations[];
  pricingModifiers?: PricingModifiers;
  draftSource?: string | null;
}

export interface AcquisitionOfferProperties {
  urlReferrer?: string;
  acquisitionCorrelationId?: string;
}

export interface OfferBuilderState {
  builderVariant: OfferBuilderVariant;
  error: OfferBuilderError | null;
  takerMarketErrors: Record<string, TakerMarketError[]>;
  frequency: OfferBuilderFrequency | null;
  pricingStrategy: OfferBuilderPricingStrategy | null;
  rate: number | null;
  rateType: RateType | null;
  schedule: Schedule;
  selected: TakerMarket[];
  workingTakerMarkets: Record<string, WorkingTakerMarket>;
  initialDisplayOrder: {
    buyers: string[];
    takerMarketsPerBuyer: Record<string, string[]>;
  } | null;
  prioritizeSelected: boolean;
  expandSelected: boolean;
  initialStep: OfferBuilderStepPath | null;
  disableNavigation: boolean;
  reviewOnly: boolean;
  submissionInProgress: boolean;
  submissionComplete: boolean;
  submissionErrors: OfferSubmissionError[];
  draftOfferProperties?: DraftOfferProperties;
  location?: OfferBuilderLocation;
  source?: string; // UI element name e.g. new-division-alert
  acquisitionOfferProperties?: AcquisitionOfferProperties;
  preferredRateSelected: boolean;
  preferredRateByMarket: Record<
    string,
    { rate: number | undefined; projectedClearingPercentage: number | undefined } | undefined
  >;
  preUpgradeRates: PreUpgradeRateEntry[];
  recurringDiscountModalShown: boolean;
}

export type OfferBuilderAction =
  | { type: 'TOGGLE_TAKER_MARKETS'; payload: TakerMarket[] }
  | { type: 'SELECT_ALL_TAKER_MARKETS'; payload: TakerMarket[] }
  | { type: 'TOGGLE_TAKER_MARKET'; payload: TakerMarket }
  | { type: 'SET_BUILDER_VARIANT'; payload: OfferBuilderVariant }
  | { type: 'SET_ERROR'; payload: OfferBuilderError }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_TAKER_MARKET_ERRORS'; payload: { takerMarketKey: string; errors: TakerMarketError[] } }
  | { type: 'SET_FREQUENCY'; payload: OfferBuilderFrequency }
  | { type: 'SET_RATE'; payload: number | null }
  | { type: 'SET_RATE_TYPE'; payload: RateType }
  | { type: 'SET_PRICING_STRATEGY'; payload: OfferBuilderPricingStrategy | null }
  | {
      type: 'UPDATE_WORKING_TAKER_MARKETS_RATE';
      payload: {
        takerMarketKey: string;
        rateInfo:
          | {
              rate: number;
              rateType: RateType;
              pricingRateUuid?: string;
              suggestedRate?: number;
              suggestRateType?: RateType;
              customRate?: number;
              customRateType?: RateType;
              pricingStrategy?: OfferBuilderPricingStrategy;
              isIndividuallyOverridden?: boolean;
            }
          | undefined;
      }[];
    }
  | {
      type: 'CHANGE_RATE_TYPE_AND_CLEAR_STRATEGY';
      payload: {
        rateType: RateType;
        nameYourRateMarkets: TakerMarket[];
      };
    }
  | {
      type: 'SET_PRICING_STRATEGY_WITH_RATES';
      payload: {
        pricingStrategy: OfferBuilderPricingStrategy;
        rateType: RateType;
        rateMap: RateMap;
      };
    }
  | {
      type: 'SET_NAME_YOUR_RATE_WITH_RATE';
      payload: {
        rate: number;
        rateType: RateType;
        pricingInformationMap?: PricingInformationMap;
      };
    }
  | { type: 'CLEAR_WORKING_STATE' }
  | { type: 'RESET' }
  | { type: 'SET_SCHEDULE'; payload: Schedule }
  | {
      type: 'SET_INITIAL_DISPLAY_ORDER';
      payload: {
        buyers: string[];
        takerMarketsPerBuyer: Record<string, string[]>;
      };
    }
  | { type: 'SET_FREQUENCY_AND_RESET_PRICING'; payload: { frequency: OfferBuilderFrequency; selected: TakerMarket[] } }
  | { type: 'HYDRATE_SELECTED'; payload: TakerMarket[] }
  | { type: 'HYDRATE_SELECTED_INITIAL'; payload: { takerMarkets: TakerMarket[]; autoSelectAll: boolean } }
  | {
      type: 'HYDRATE_DRAFT_OFFER_RATE_INFORMATIONS';
      payload: { rateInformations: RateInformations[]; rateType: RateType };
    }
  | { type: 'START_SUBMISSION' }
  | { type: 'SET_SUBMISSION_COMPLETE'; payload: { errors: OfferSubmissionError[] } }
  | { type: 'RESET_SUBMISSION_STATE' }
  | { type: 'SET_PREFERRED_RATE_SELECTED'; payload: boolean }
  | {
      type: 'SET_PREFERRED_RATES_BY_MARKET';
      payload: Record<
        string,
        { rate: number | undefined; projectedClearingPercentage: number | undefined } | undefined
      >;
    }
  | { type: 'SET_PRE_UPGRADE_RATES'; payload: PreUpgradeRateEntry[] }
  | { type: 'CLEAR_PREFERRED_RATE_STATE' }
  | { type: 'SET_RECURRING_DISCOUNT_MODAL_SHOWN' };

export const initialOfferBuilderState: OfferBuilderState = {
  builderVariant: 'standard',
  error: null,
  takerMarketErrors: {},
  frequency: null,
  pricingStrategy: null,
  rate: null,
  rateType: null,
  schedule: defaultSchedule,
  selected: [],
  workingTakerMarkets: {},
  initialDisplayOrder: null,
  prioritizeSelected: false,
  expandSelected: false,
  initialStep: null,
  disableNavigation: false,
  reviewOnly: false,
  submissionInProgress: false,
  submissionComplete: false,
  submissionErrors: [],
  preferredRateSelected: false,
  preferredRateByMarket: {},
  preUpgradeRates: [],
  recurringDiscountModalShown: false,
};

export interface CreateOfferBuilderInitialStateOptions {
  builderVariant?: OfferBuilderVariant;
  frequency?: OfferBuilderFrequency | null;
  pricingStrategy?: OfferBuilderPricingStrategy | null;
  rate?: number | null;
  rateType?: RateType | null;
  schedule?: Schedule;
  selected?: TakerMarket[];
  workingTakerMarkets?: Record<string, WorkingTakerMarket>;
  prioritizeSelected?: boolean;
  expandSelected?: boolean;
  initialStep?: OfferBuilderStepPath | null;
  disableNavigation?: boolean;
  reviewOnly?: boolean;
  initialDisplayOrder?: {
    buyers: string[];
    takerMarketsPerBuyer: Record<string, string[]>;
  } | null;
  draftOfferProperties?: DraftOfferProperties;
  location?: OfferBuilderLocation;
  source?: string;
  acquisitionOfferProperties?: AcquisitionOfferProperties;
}

/**
 * creates an initial state object for OfferBuilderProvider
 * merges provided options with initialOfferBuilderState defaults
 */
export const createOfferBuilderInitialState = (
  options: CreateOfferBuilderInitialStateOptions = {}
): OfferBuilderState => {
  return {
    ...initialOfferBuilderState,
    ...options,
  };
};

const offerBuilderReducer = (state: OfferBuilderState, action: OfferBuilderAction): OfferBuilderState => {
  switch (action.type) {
    case 'SELECT_ALL_TAKER_MARKETS': {
      // check if all provided taker markets are already selected
      const allAlreadySelected = action.payload.every((takerMarket) => state.selected.includes(takerMarket));

      let newSelected: TakerMarket[];

      if (allAlreadySelected) {
        // if all are selected, deselect all provided taker markets
        newSelected = state.selected.filter((takerMarket) => !action.payload.includes(takerMarket));
      } else {
        // if not all are selected, select all provided taker markets
        const existingSelected = new Set(state.selected);
        newSelected = [...state.selected];

        // add any taker markets that aren't already selected
        action.payload.forEach((takerMarket) => {
          if (!existingSelected.has(takerMarket)) {
            newSelected.push(takerMarket);
          }
        });
      }

      const newSelectedKeys = new Set(newSelected.map(buildTakerMarketKey));
      const filteredWorkingTakerMarkets = Object.fromEntries(
        Object.entries(state.workingTakerMarkets).filter(([key]) => newSelectedKeys.has(key))
      );
      const newWorkingTakerMarkets = ensureWorkingTakerMarkets(filteredWorkingTakerMarkets, newSelected);
      const resetWorkingTakerMarkets = resetRatesForWorkingTakerMarkets(newWorkingTakerMarkets, newSelected);

      return {
        ...state,
        selected: newSelected,
        error: null,
        takerMarketErrors: {},
        pricingStrategy: null,
        workingTakerMarkets: resetWorkingTakerMarkets,
        preferredRateSelected: false,
        preferredRateByMarket: {},
        preUpgradeRates: [],
      };
    }

    case 'TOGGLE_TAKER_MARKETS': {
      const takerMarketsExists = action.payload.some((takerMarket) => state.selected.includes(takerMarket));
      const newSelected = takerMarketsExists
        ? state.selected.filter((takerMarket) => !action.payload.includes(takerMarket))
        : [...state.selected, ...action.payload];

      return {
        ...state,
        selected: newSelected,
        error: null,
        takerMarketErrors: {},
        pricingStrategy: null,
        workingTakerMarkets: computeWorkingTakerMarketsAfterToggle(
          state.workingTakerMarkets,
          newSelected,
          takerMarketsExists
        ),
        preferredRateSelected: false,
        preferredRateByMarket: {},
        preUpgradeRates: [],
      };
    }

    case 'TOGGLE_TAKER_MARKET': {
      const takerMarketExists = state.selected.includes(action.payload);
      const updatedSelected = takerMarketExists
        ? state.selected.filter((takerMarket) => takerMarket !== action.payload)
        : [...state.selected, action.payload];

      return {
        ...state,
        selected: updatedSelected,
        error: null,
        takerMarketErrors: {},
        pricingStrategy: null,
        workingTakerMarkets: computeWorkingTakerMarketsAfterToggle(
          state.workingTakerMarkets,
          updatedSelected,
          takerMarketExists
        ),
        preferredRateSelected: false,
        preferredRateByMarket: {},
        preUpgradeRates: [],
      };
    }

    case 'SET_BUILDER_VARIANT': {
      return {
        ...state,
        builderVariant: action.payload,
      };
    }

    case 'SET_ERROR': {
      return {
        ...state,
        error: action.payload,
      };
    }

    case 'CLEAR_ERROR': {
      return {
        ...state,
        error: null,
      };
    }

    case 'SET_TAKER_MARKET_ERRORS': {
      const { takerMarketKey, errors } = action.payload;
      return {
        ...state,
        takerMarketErrors: {
          ...state.takerMarketErrors,
          [takerMarketKey]: errors,
        },
      };
    }

    case 'SET_FREQUENCY': {
      return {
        ...state,
        frequency: action.payload,
      };
    }

    case 'SET_FREQUENCY_AND_RESET_PRICING': {
      const { frequency, selected } = action.payload;
      const nameYourRateMarkets = getNameYourRateMarkets(selected);
      const resetWorkingTakerMarkets = resetRatesForWorkingTakerMarkets(state.workingTakerMarkets, nameYourRateMarkets);

      return {
        ...state,
        frequency,
        workingTakerMarkets: resetWorkingTakerMarkets,
        takerMarketErrors: {},
        pricingStrategy: null,
        preferredRateSelected: false,
        preferredRateByMarket: {},
      };
    }

    case 'SET_RATE': {
      return {
        ...state,
        rate: action.payload,
      };
    }

    case 'SET_RATE_TYPE': {
      return {
        ...state,
        rateType: action.payload,
      };
    }

    case 'SET_PRICING_STRATEGY': {
      return {
        ...state,
        pricingStrategy: action.payload,
      };
    }

    case 'SET_SCHEDULE': {
      return {
        ...state,
        schedule: action.payload,
      };
    }

    case 'UPDATE_WORKING_TAKER_MARKETS_RATE': {
      const rateUpdates = action.payload;
      const rateMap = Object.fromEntries(rateUpdates.map(({ takerMarketKey, rateInfo }) => [takerMarketKey, rateInfo]));
      const updatedWorkingTakerMarkets = updateWorkingTakerMarketsWithRates(state.workingTakerMarkets, rateMap);
      // clear errors for taker markets that have rates
      const marketsWithRates = rateUpdates
        .filter(({ rateInfo }) => rateInfo?.rate !== undefined && rateInfo?.rate !== null)
        .map(({ takerMarketKey }) => takerMarketKey);
      const updatedTakerMarketErrors = clearTakerMarketErrorsForKeys(
        state.takerMarketErrors,
        marketsWithRates,
        'NO_RATE_SELECTED'
      );

      return {
        ...state,
        takerMarketErrors: updatedTakerMarketErrors,
        workingTakerMarkets: updatedWorkingTakerMarkets,
      };
    }

    case 'CLEAR_WORKING_STATE': {
      return {
        ...state,
        workingTakerMarkets: {},
      };
    }

    case 'SET_INITIAL_DISPLAY_ORDER': {
      return {
        ...state,
        initialDisplayOrder: action.payload,
      };
    }

    case 'HYDRATE_SELECTED': {
      const freshTakerMarkets = action.payload;
      const selectedIds = new Set(state.selected.map((tm) => tm.id));
      const updatedSelected = freshTakerMarkets.filter((tm) => selectedIds.has(tm.id));
      // preserve rate state — this fires on background refetches only; submission skips hydration via skipHydrationRef
      const hydratedWorkingTakerMarkets = filterBySelectedTakerMarkets(state.workingTakerMarkets, updatedSelected);

      return {
        ...state,
        selected: updatedSelected,
        error: null,
        takerMarketErrors: {},
        pricingStrategy: null,
        workingTakerMarkets: hydratedWorkingTakerMarkets,
      };
    }

    case 'HYDRATE_SELECTED_INITIAL': {
      const { takerMarkets: freshTakerMarkets, autoSelectAll } = action.payload;
      const selectedIds = new Set(state.selected.map((tm) => tm.id));

      // when no predefined selection exists and autoSelectAll is set, default to selecting all available markets;
      // markets with an active offer are excluded so users opt-in to overriding an existing offer
      const selectableMarkets = freshTakerMarkets.filter((tm) => !tm.offerConfig.isEnabled && !tm.offerSchedule);

      const updatedSelected =
        autoSelectAll && selectedIds.size === 0
          ? selectableMarkets
          : freshTakerMarkets.filter((tm) => selectedIds.has(tm.id));

      const newWorkingTakerMarkets =
        autoSelectAll && selectedIds.size === 0
          ? ensureWorkingTakerMarkets(state.workingTakerMarkets, updatedSelected)
          : state.workingTakerMarkets;

      return {
        ...state,
        selected: updatedSelected,
        workingTakerMarkets: newWorkingTakerMarkets,
      };
    }

    case 'HYDRATE_DRAFT_OFFER_RATE_INFORMATIONS': {
      const { rateInformations, rateType } = action.payload;

      if (!state.draftOfferProperties) {
        return state;
      }

      return {
        ...state,
        rateType,
        draftOfferProperties: {
          ...state.draftOfferProperties,
          rateInformations,
        },
      };
    }

    case 'CHANGE_RATE_TYPE_AND_CLEAR_STRATEGY': {
      const { rateType, nameYourRateMarkets } = action.payload;
      // reset working taker markets with undefined rates
      const rateMap = createUniformRateMap(nameYourRateMarkets, undefined);
      const updatedWorkingTakerMarkets = updateWorkingTakerMarketsWithRates(state.workingTakerMarkets, rateMap);

      return {
        ...state,
        rateType,
        pricingStrategy: null,
        rate: null,
        takerMarketErrors: {},
        workingTakerMarkets: updatedWorkingTakerMarkets,
        preferredRateSelected: false,
        preferredRateByMarket: {},
      };
    }

    case 'SET_PRICING_STRATEGY_WITH_RATES': {
      const { pricingStrategy, rateType, rateMap } = action.payload;
      const updatedWorkingTakerMarkets = updateWorkingTakerMarketsWithRates(state.workingTakerMarkets, rateMap);

      return {
        ...state,
        rateType,
        pricingStrategy,
        workingTakerMarkets: updatedWorkingTakerMarkets,
      };
    }

    case 'SET_NAME_YOUR_RATE_WITH_RATE': {
      const { rate, rateType, pricingInformationMap } = action.payload;
      const nameYourRateMarkets = getNameYourRateMarkets(state.selected);
      const rateMap = createMixedRateMap(nameYourRateMarkets, rate, rateType, pricingInformationMap);
      const updatedWorkingTakerMarkets = updateWorkingTakerMarketsWithRates(state.workingTakerMarkets, rateMap);

      return {
        ...state,
        rate,
        rateType,
        pricingStrategy: 'nameYourRate',
        workingTakerMarkets: updatedWorkingTakerMarkets,
      };
    }

    case 'START_SUBMISSION': {
      return {
        ...state,
        submissionInProgress: true,
      };
    }

    case 'SET_SUBMISSION_COMPLETE': {
      return {
        ...state,
        submissionInProgress: false,
        submissionComplete: true,
        submissionErrors: action.payload.errors,
      };
    }

    case 'RESET_SUBMISSION_STATE': {
      return {
        ...state,
        submissionInProgress: false,
        submissionComplete: false,
        submissionErrors: [],
      };
    }

    case 'SET_PREFERRED_RATE_SELECTED': {
      return {
        ...state,
        preferredRateSelected: action.payload,
      };
    }

    case 'SET_PREFERRED_RATES_BY_MARKET': {
      return {
        ...state,
        preferredRateByMarket: action.payload,
      };
    }

    case 'SET_PRE_UPGRADE_RATES': {
      return {
        ...state,
        preUpgradeRates: action.payload,
      };
    }

    case 'CLEAR_PREFERRED_RATE_STATE': {
      const preUpgradeRateMap = Object.fromEntries(
        state.preUpgradeRates.map(({ takerMarketKey, rateInfo }) => [takerMarketKey, rateInfo])
      );
      const restoredWorkingTakerMarkets = updateWorkingTakerMarketsWithRates(
        state.workingTakerMarkets,
        preUpgradeRateMap
      );

      return {
        ...state,
        pricingStrategy: null,
        takerMarketErrors: {},
        workingTakerMarkets: restoredWorkingTakerMarkets,
        preferredRateSelected: false,
        preferredRateByMarket: {},
        preUpgradeRates: [],
      };
    }

    case 'RESET': {
      return { ...state, ...initialOfferBuilderState };
    }

    case 'SET_RECURRING_DISCOUNT_MODAL_SHOWN': {
      return { ...state, recurringDiscountModalShown: true };
    }

    default:
      return state;
  }
};

export interface OfferBuilderContextValue {
  dispatch: React.Dispatch<OfferBuilderAction>;
  state: OfferBuilderState;
  takerMarkets: TakerMarket[];
}

const OfferBuilderContext = createContext<OfferBuilderContextValue | null>(null);

/**
 * Reorders buyer groups and taker markets to prioritize selected items.
 * - Selected taker markets are moved to the top of their buyer group and sorted by eligibleInvoiceAmount (high to low).
 * - Buyers with selections are moved to the top, sorted by total selected eligible invoice amount (high to low).
 * - Buyers without selections maintain their original order and appear below selected buyers.
 */
export const createPrioritizedDisplayOrder = (
  buyers: Array<{ makerOrganizationUuid: string; makerOrganizationName: string; takerMarkets: TakerMarket[] }>,
  selectedIds: Set<string>
): { buyers: string[]; takerMarketsPerBuyer: Record<string, string[]> } => {
  const buyersWithMetadata = buyers.map((buyer, originalIndex) => {
    let selectedAmount = 0;
    let totalAmount = 0;
    const selectedMarkets: TakerMarket[] = [];
    const unselectedMarkets: TakerMarket[] = [];

    for (const tm of buyer.takerMarkets) {
      const amount = tm.eligibleInvoiceAmountUsd;
      totalAmount += amount;

      if (selectedIds.has(tm.id)) {
        selectedAmount += amount;
        selectedMarkets.push(tm);
      } else {
        unselectedMarkets.push(tm);
      }
    }

    return {
      buyer,
      selectedAmount,
      totalAmount,
      selectedMarkets,
      unselectedMarkets,
      originalIndex,
    };
  });

  // sort buyers: selected first (by amount desc), then non-selected (maintain original order)
  buyersWithMetadata.sort((a, b) => {
    const aHasSelection = a.selectedMarkets.length > 0;
    const bHasSelection = b.selectedMarkets.length > 0;

    // if one has selections and the other doesn't, prioritize the one with selections
    if (aHasSelection !== bHasSelection) {
      return aHasSelection ? -1 : 1;
    }

    // if both have selections, sort by selected amount (high to low)
    if (aHasSelection && bHasSelection) {
      return b.selectedAmount - a.selectedAmount;
    }

    // if neither has selections, maintain original order
    return a.originalIndex - b.originalIndex;
  });

  const takerMarketsPerBuyer = Object.fromEntries(
    buyersWithMetadata.map((item) => {
      const sortedSelected = sortTakerMarkets(item.selectedMarkets, {
        key: 'eligibleInvoiceAmount',
        direction: 'desc',
      });

      return [
        item.buyer.makerOrganizationUuid,
        [...sortedSelected.map((tm) => tm.id), ...item.unselectedMarkets.map((tm) => tm.id)],
      ];
    })
  );

  return {
    buyers: buyersWithMetadata.map((item) => item.buyer.makerOrganizationUuid),
    takerMarketsPerBuyer,
  };
};

export const OfferBuilderProvider = ({
  children,
  initialState,
  takerMarkets,
}: {
  children: ReactNode;
  initialState?: OfferBuilderState;
  takerMarkets: TakerMarket[];
}) => {
  const [state, dispatch] = useReducer(offerBuilderReducer, {
    ...initialOfferBuilderState,
    ...initialState,
  });
  const initialBuyers = useGroupByBuyer({ takerMarkets, sortByEligible: true });
  const hasInitiallyHydrated = useRef(false);
  const previousTakerMarketsRef = useRef<TakerMarket[]>();
  const skipHydrationRef = useRef(false);
  const autoSelectAllRef = useRef(!initialState);

  // capture initial order when data first loads
  useEffectOnce(() => {
    if (!state.initialDisplayOrder && initialBuyers.length) {
      let initialOrder;

      if (state.prioritizeSelected && state.selected.length > 0) {
        // when prioritizeSelected is true, reorder to put selected items at the top
        const selectedIds = new Set(state.selected.map((tm) => tm.id));
        initialOrder = createPrioritizedDisplayOrder(initialBuyers, selectedIds);
      } else {
        // preserve original order from useGroupByBuyer
        initialOrder = {
          buyers: initialBuyers.map((buyer) => buyer.makerOrganizationUuid),
          takerMarketsPerBuyer: Object.fromEntries(
            initialBuyers.map((buyer) => [buyer.makerOrganizationUuid, buyer.takerMarkets.map((tm) => tm.id)])
          ),
        };
      }

      dispatch({ type: 'SET_INITIAL_DISPLAY_ORDER', payload: initialOrder });
    }
  });

  // wrap dispatch to track offer submit start/completion in ref
  const wrappedDispatch = (action: OfferBuilderAction) => {
    if (action.type === 'START_SUBMISSION') {
      skipHydrationRef.current = true;
    }

    if (action.type === 'SET_SUBMISSION_COMPLETE') {
      skipHydrationRef.current = true;
    }

    if (action.type === 'RESET_SUBMISSION_STATE') {
      skipHydrationRef.current = false;
    }

    dispatch(action);
  };

  useEffect(() => {
    if (takerMarkets?.length) {
      // check if this is the first time we're seeing takerMarkets
      if (!hasInitiallyHydrated.current) {
        // first hydration; auto-select all when no initialState was provided (clean entry into offer builder)
        dispatch({
          type: 'HYDRATE_SELECTED_INITIAL',
          payload: { takerMarkets, autoSelectAll: autoSelectAllRef.current },
        });
        hasInitiallyHydrated.current = true;
        previousTakerMarketsRef.current = takerMarkets;
      } else if (previousTakerMarketsRef.current !== takerMarkets && !skipHydrationRef.current) {
        // only run HYDRATE_SELECTED if takerMarkets reference actually changed
        // skip hydration after submission to preserve rates on confirmation page
        dispatch({ type: 'HYDRATE_SELECTED', payload: takerMarkets });
        previousTakerMarketsRef.current = takerMarkets;
      }
    }
  }, [takerMarkets]);

  const value = {
    state,
    dispatch: wrappedDispatch,
    takerMarkets,
  };

  return <OfferBuilderContext.Provider value={value}>{children}</OfferBuilderContext.Provider>;
};

export const useOfferBuilderContext = () => {
  const context = useContext(OfferBuilderContext);

  if (!context) {
    throw new Error('useOfferBuilderContext must be used within OfferBuilderProvider');
  }

  return context;
};
